import {List, RecordOf} from 'immutable';
import {createAction, ActionType} from '@packages/utilities/create-action';
import {
  COMMERCE_CORRUPTED_PRODUCT_DETECTED,
  COMMERCE_CORRUPTED_VARIANTS_FIXED,
  COMMERCE_CONNECT_SUCCEEDED,
  COMMERCE_ITEM_CREATION_RECOVERY_STARTED,
  COMMERCE_ITEM_SAVED,
  COMMERCE_PRODUCT_TYPE_CHANGED,
  DEFAULT_VARIANT_UPDATED,
  PRODUCTS_LIST_CHANGED,
  PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED,
  PRODUCT_OPTION_SET_OPTION_ADDED,
  PRODUCT_OPTION_SET_OPTION_REMOVED,
  PRODUCT_OPTION_SET_REMOVED,
  PRODUCT_OPTION_SET_UNMOUNTED,
  PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED,
  SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED,
  DYN_VARIANT_MODIFIED,
  DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED,
  PRODUCT_ITEM_META_MODIFIED,
  COMMERCE_INCLUDE_DOWNLOADS_TOGGLED,
} from '@packages/systems/designer/actions';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';
import {
  InventoryWithId,
  Variant,
  VariantChange,
  VariantsByOptionCombinationHash,
} from '@packages/systems/commerce/core';
import {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {PublicDatabase} from '@packages/systems/cms/types';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';

export const changeProductList = createAction<
  typeof PRODUCTS_LIST_CHANGED,
  {
    value: List<string>;
    formId: string;
    keyPath: string[];
    recordId: string;
  }
>(PRODUCTS_LIST_CHANGED);

export const commerceConnected = createAction<
  typeof COMMERCE_CONNECT_SUCCEEDED,
  {
    response: RecordOf<{
      collections: List<CollectionRecordType>;
      db: RecordOf<PublicDatabase>;
      pages: List<PageRecordType>;
    }>;
  }
>(COMMERCE_CONNECT_SUCCEEDED);

export type CommerceConnectedSucceededAction = DesignerActionFromCreator<
  typeof commerceConnected
>;

export const commerceCorruptedVariantsFixed = createAction<
  typeof COMMERCE_CORRUPTED_VARIANTS_FIXED,
  {
    item: ItemRecordType;
  }
>(COMMERCE_CORRUPTED_VARIANTS_FIXED);

export type CommerceCorruptedVariantsFixedAction = DesignerActionFromCreator<
  typeof commerceCorruptedVariantsFixed
>;

// CC-240: signals that a corrupted product (orphaned default-sku) was
// detected upstream. ProductItemStore resets `currentItemMeta` so the
// user can't continue editing on top of stale state and accidentally
// re-persist the orphan reference on save.
export const commerceCorruptedProductDetected = createAction<
  typeof COMMERCE_CORRUPTED_PRODUCT_DETECTED,
  {
    itemId: string;
    defaultSkuId?: string;
    collectionId?: string;
  }
>(COMMERCE_CORRUPTED_PRODUCT_DETECTED);

export type CommerceCorruptedProductDetectedAction = DesignerActionFromCreator<
  typeof commerceCorruptedProductDetected
>;

export const commerceIncludeDownloadsToggled = createAction<
  typeof COMMERCE_INCLUDE_DOWNLOADS_TOGGLED,
  {
    value: boolean;
    id: string;
  }
>(COMMERCE_INCLUDE_DOWNLOADS_TOGGLED);

export type CommerceIncludeDownloadsToggledAction = DesignerActionFromCreator<
  typeof commerceIncludeDownloadsToggled
>;

export const commerceItemCreationRecoveryStarted = createAction<
  typeof COMMERCE_ITEM_CREATION_RECOVERY_STARTED,
  {
    collection: CollectionRecordType;
    item: ItemRecordType;
    failedItem: ItemRecordType;
  }
>(COMMERCE_ITEM_CREATION_RECOVERY_STARTED);

export type CommerceItemCreationRecoveryStartedAction =
  DesignerActionFromCreator<typeof commerceItemCreationRecoveryStarted>;

export const commerceProductTypeChanged = createAction<
  typeof COMMERCE_PRODUCT_TYPE_CHANGED,
  {
    conflictingFieldSlugs: string[];
  }
>(COMMERCE_PRODUCT_TYPE_CHANGED);

export type CommerceProductTypeChangedAction = ActionType<
  typeof commerceProductTypeChanged
>;

export const dynVariantModified = createAction<
  typeof DYN_VARIANT_MODIFIED,
  VariantChange & {
    formId: string;
  }
>(DYN_VARIANT_MODIFIED);

export type DynVariantModifiedAction = DesignerActionFromCreator<
  typeof dynVariantModified
>;

export const dynVariantImageUploadProgressed = createAction<
  typeof DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED,
  {
    keyPath: Array<string | number>;
    value: number | null | undefined;
  }
>(DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED);

export const defaultVariantUpdated = createAction<
  typeof DEFAULT_VARIANT_UPDATED,
  {
    id: string;
  }
>(DEFAULT_VARIANT_UPDATED);

export type DefaultVariantUpdatedAction = DesignerActionFromCreator<
  typeof defaultVariantUpdated
>;

export const generateSampleProducts = createAction<
  typeof SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED,
  {
    productsCreatedCount: number;
    skusCreatedCount: number;
    collectionId: {
      product: string;
      sku: string;
    };
  }
>(SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED);

export type SampleProductItemsGenerationSucceededAction =
  DesignerActionFromCreator<typeof generateSampleProducts>;

export const productItemMetaModified = createAction<
  typeof PRODUCT_ITEM_META_MODIFIED,
  {
    keyPath: string[];
    value: string | number | VariantsByOptionCombinationHash;
  }
>(PRODUCT_ITEM_META_MODIFIED);

export type ProductItemMetaModifiedAction = DesignerActionFromCreator<
  typeof productItemMetaModified
>;

export const productItemInventoriesFetched = createAction<
  typeof PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED,
  {
    response: {
      inventories: InventoryWithId[];
    };
  }
>(PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED);

export type ProductItemInventoriesFetchSucceededAction = ActionType<
  typeof productItemInventoriesFetched
>;

export const productOptionSetRemoved = createAction<
  typeof PRODUCT_OPTION_SET_REMOVED,
  {
    optionSetId: string;
    defaultOptionId?: string;
  }
>(PRODUCT_OPTION_SET_REMOVED);

export type ProductOptionSetRemovedAction = DesignerActionFromCreator<
  typeof productOptionSetRemoved
>;

export const productOptionSetWithDefaultOptionCreated = createAction<
  typeof PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED,
  {
    optionSetId: string;
    optionId: string;
  }
>(PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED);

export const productOptionSetOptionAdded = createAction<
  typeof PRODUCT_OPTION_SET_OPTION_ADDED,
  {
    optionSetId: string;
    optionId: string;
    variantMintIds: string[];
  }
>(PRODUCT_OPTION_SET_OPTION_ADDED);

export const productOptionSetOptionRemoved = createAction<
  typeof PRODUCT_OPTION_SET_OPTION_REMOVED,
  {
    optionSetId: string;
    optionId: string;
    defaultOptionId?: string;
  }
>(PRODUCT_OPTION_SET_OPTION_REMOVED);

export const productOptionSetUnmounted = createAction<
  typeof PRODUCT_OPTION_SET_UNMOUNTED,
  {
    optionSetIndex: number;
    item: ItemRecordType;
  }
>(PRODUCT_OPTION_SET_UNMOUNTED);

export type ProductOptionSetUnmountedAction = DesignerActionFromCreator<
  typeof productOptionSetUnmounted
>;

export const saveCommerceItem = createAction<
  typeof COMMERCE_ITEM_SAVED,
  {
    item: ItemRecordType;
    subscriptionsCreated?: Variant[];
    fromInline?: boolean;
    isCategoryItem?: boolean;
  }
>(COMMERCE_ITEM_SAVED);

export type CommerceItemSavedAction = DesignerActionFromCreator<
  typeof saveCommerceItem
>;
